import { useState, useEffect } from 'react';
import { Button } from '../ui';
import { useLLMProviders } from '../../hooks/useLLMProviders';
import { useSettingsUIStore } from '../../store/settingsUIStore';
import { getProviderLogoPath, getProviderLogoAlt } from '../../utils/providerLogos';

export function LLMProviderSettings() {
  const {
    providers,
    activeProvider,
    isLoading: isProvidersLoading,
    updateProvider,
    activateProvider,
    isUpdating: isProviderSaving,
  } = useLLMProviders();

  const {
    selectedProviderId,
    isEditingProvider,
    selectProvider: selectProviderId,
    setEditingProvider,
  } = useSettingsUIStore();

  const [providerConfig, setProviderConfig] = useState({
    model: '',
    temperature: 0.1,
  });

  const selectedProvider = providers.find((p) => p.id === selectedProviderId) || null;

  // Auto-select active provider on load
  useEffect(() => {
    if (activeProvider && !selectedProviderId) {
      selectProviderId(activeProvider.id);
    }
  }, [activeProvider, selectedProviderId, selectProviderId]);

  // Initialize provider config when selected provider changes
  useEffect(() => {
    if (selectedProvider) {
      setProviderConfig({
        model: selectedProvider.model,
        temperature: selectedProvider.temperature,
      });
    }
  }, [selectedProvider]);

  const handleSelectProvider = (providerId: number) => {
    selectProviderId(providerId);
    setEditingProvider(false);
  };

  const handleSaveProviderConfig = async () => {
    if (!selectedProvider) return;
    updateProvider({ id: selectedProvider.id, updates: providerConfig });
    setEditingProvider(false);
  };

  const handleActivateProvider = async (providerId: number) => {
    activateProvider(providerId);
  };

  if (isProvidersLoading) {
    return (
      <div className="bg-white dark:bg-dark-elevated rounded-xl border border-gray-200 dark:border-dark-border p-3 mb-4">
        <div className="flex items-center justify-center py-4 text-gray-500 dark:text-gray-400">
          <span className="inline-block w-5 h-5 border-2 border-purple-400 border-t-transparent rounded-full animate-spin mr-2" />
          Loading providers...
        </div>
      </div>
    );
  }

  if (providers.length === 0) {
    return (
      <div className="bg-white dark:bg-dark-elevated rounded-xl border border-gray-200 dark:border-dark-border p-3 mb-4">
        <div className="text-center py-4 text-gray-500 dark:text-gray-400">
          No providers found. Please check database seeding.
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-dark-elevated rounded-xl border border-gray-200 dark:border-dark-border p-3 mb-4">
      <div className="flex items-center gap-4">
        {/* Provider Selector */}
        <div className="flex gap-2">
          {providers.map((provider) => (
            <button
              key={provider.id}
              onClick={() => handleSelectProvider(provider.id)}
              data-onboarding={`provider-${provider.name.toLowerCase()}`}
              className={`px-3 py-2 rounded-lg border-2 transition-all flex items-center gap-2 ${
                selectedProviderId === provider.id
                  ? 'border-purple-400 bg-purple-50 dark:bg-purple-900/30'
                  : 'border-gray-200 dark:border-dark-border hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-dark-elevated'
              }`}
            >
              <img
                src={getProviderLogoPath(provider.name)}
                alt={getProviderLogoAlt(provider.name)}
                className="w-6 h-6 object-contain shrink-0 dark:brightness-0 dark:invert"
              />
              <div className="text-left">
                <div className="flex items-center gap-1.5">
                  <span className="font-semibold text-xs text-gray-800 dark:text-gray-100">{provider.display_name}</span>
                  {provider.is_active && (
                    <span className="px-1.5 py-0.5 bg-purple-500 text-white text-[9px] rounded-full">Active</span>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>

        <div className="h-8 w-px bg-gray-200 dark:bg-[#2A2A3C]" />

        {/* Configuration */}
        {selectedProvider && (
          <>
            <div className="flex-1 flex items-center gap-3">
              <div className="flex items-center gap-2" data-onboarding="model-input">
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">Model:</label>
                <input
                  type="text"
                  value={providerConfig.model}
                  onChange={(e) => setProviderConfig({ ...providerConfig, model: e.target.value })}
                  disabled={!isEditingProvider}
                  className={`px-2 py-1 text-xs rounded border border-gray-300 dark:border-dark-border focus:border-purple-400 focus:ring-1 focus:ring-purple-400/40 outline-none font-mono text-gray-800 dark:text-gray-200 ${
                    !isEditingProvider ? 'bg-gray-50 dark:bg-dark-recessed cursor-not-allowed' : 'bg-white dark:bg-dark-elevated'
                  }`}
                  style={{ width: '200px' }}
                />
              </div>

              <div className="flex items-center gap-2" data-onboarding="temperature-slider">
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">
                  Temp: {providerConfig.temperature.toFixed(1)}
                </label>
                <input
                  type="range"
                  min="0"
                  max="2"
                  step="0.1"
                  value={providerConfig.temperature}
                  onChange={(e) => setProviderConfig({ ...providerConfig, temperature: parseFloat(e.target.value) })}
                  disabled={!isEditingProvider}
                  className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer disabled:cursor-not-allowed"
                  style={{ width: '120px' }}
                />
              </div>
            </div>

            <div className="flex items-center gap-1.5">
              {isEditingProvider ? (
                <>
                  <Button variant="primary" size="sm" onClick={handleSaveProviderConfig} isLoading={isProviderSaving}>
                    Save
                  </Button>
                  <Button variant="secondary" size="sm" onClick={() => setEditingProvider(false)}>
                    Cancel
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="secondary" size="sm" onClick={() => setEditingProvider(true)} data-onboarding="provider-edit-btn">
                    Edit
                  </Button>
                  {!selectedProvider.is_active && (
                    <Button variant="success" size="sm" onClick={() => handleActivateProvider(selectedProvider.id)} data-onboarding="provider-activate-btn">
                      Set Active
                    </Button>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
